import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * "Only N of M pages were read."
 *
 * Vision transcription is capped at VISION_PAGE_CAP pages. Before this existed, a 5-page invoice
 * whose last two pages were never looked at was indistinguishable from a fully-read 3-page one:
 * same status, same confidence, no flag. If the total sat on page 4, the extracted amount was
 * simply wrong and nothing said so.
 *
 * It renders NEXT TO THE AMOUNT deliberately. "3 of 5 pages" in a corner is a fact nobody acts on;
 * beside the number it qualifies, it is a reason to open the PDF.
 *
 * `compact` is for dense list rows — icon plus "3/5 sider" — while the full form carries the
 * consequence, which is the part that actually changes what the accountant does.
 */
export function isTruncated(doc) {
    if (!doc) return false;
    const { pages_total: total, pages_read: read } = doc;
    return Boolean(total && read && total > read);
}

export default function PagesWarning({ doc, compact = false, className = '' }) {
    if (!isTruncated(doc)) return null;
    const { pages_total: total, pages_read: read } = doc;

    if (compact) {
        return (
            <span
                data-testid="pages-warning-compact"
                title={doc.pages_warning || `Kun ${read} af ${total} sider blev læst`}
                className={`inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-amber-900 ${className}`}
            >
                <AlertTriangle className="h-3 w-3 shrink-0" />
                {read}/{total} sider
            </span>
        );
    }

    return (
        <div
            data-testid="pages-warning"
            className={`flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 ${className}`}
        >
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div className="min-w-0">
                <p className="font-medium">Kun {read} af {total} sider blev læst</p>
                <p className="mt-0.5">
                    Beløb og linjer på de sidste sider er <strong>ikke</strong> med. Åbn bilaget og
                    kontrollér totalen før du godkender.
                </p>
            </div>
        </div>
    );
}
